import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/joy';
import { keyframes } from '@emotion/react';
import confetti from 'canvas-confetti';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

interface SuccessMessageProps {
  open?: boolean;
  onClose?: () => void;
  title?: string;
  message?: string;
  subtitle?: string;
  showConfetti?: boolean;
  duration?: number;
  onComplete?: () => void;
}

export const SuccessMessage: React.FC<SuccessMessageProps> = ({
  open = true,
  onClose,
  title = 'Success!',
  message,
  subtitle,
  showConfetti = true,
  duration = 3000,
  onComplete,
}) => {
  if (!open) return null;
  
  const displaySubtitle = subtitle || message;
  useEffect(() => {
    if (showConfetti) {
      // Fire confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
    }

    const callback = onComplete || onClose;
    if (duration && callback) {
      const timer = setTimeout(callback, duration);
      return () => clearTimeout(timer);
    }
  }, [showConfetti, duration, onComplete]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        textAlign: 'center',
        zIndex: 9999,
        animation: `${fadeIn} 0.5s ease-out`,
      }}
    >
      <Typography
        level="h1"
        sx={{
          fontSize: 48,
          fontWeight: 'bold',
          color: 'success.500',
          mb: 2,
          animation: `${pulse} 1s ease-in-out infinite`,
        }}
      >
        {title}
      </Typography>
      {displaySubtitle && (
        <Typography level="body-lg" sx={{ color: 'text.secondary' }}>
          {displaySubtitle}
        </Typography>
      )}
    </Box>
  );
};
